import Project from 'src/entities/workspaceProject';
import { useCallback, useState } from 'react';
import { textEndEllipsis } from 'src/lib/textTruncate';
import User from 'src/entities/user';
import moment from 'moment';
import { trackProjectAccessedAsManager } from 'src/services/tracker/trackers/workspaces';

import useDeleteWorkspaceProject from './useDeleteWorkspaceProject';
import useLeaveWorkspaceProject from './useLeaveWorkspaceProject';
import useAccessProjectAsManager from './useAccessProjectAsManager';

type UseWorkspaceProjectListItemProps = {
	project: Project;
	currentUserIsWorkspaceOwner: boolean;
};

const useWorkspaceProjectListItem = ( { project, currentUserIsWorkspaceOwner }: UseWorkspaceProjectListItemProps ) => {
	const usersCount = project.users?.length;
	const [ showingShareProjectModal, setShowingShareProjectModal ] = useState( false );

	const isMemberOfTheProject = project.currentUserIsMember;
	const isOwnerOfTheProject = project.currentUserIsOwner;
	const currentUserProjectPermissions = project.userPermissions;

	const canAccessProjectAsManager = currentUserIsWorkspaceOwner && !isMemberOfTheProject;
	const canDeleteProject = currentUserIsWorkspaceOwner || currentUserProjectPermissions.currentUserCan( 'destroy', 'projects' );
	const canLeaveProject = isMemberOfTheProject && !isOwnerOfTheProject;
	const canShareProject = currentUserProjectPermissions.currentUserCan( 'share', 'projects' );

	const createdAt = moment( project.createdAt ).format( 'MM/DD/YY' );
	const projectName = textEndEllipsis( project.name, 33 );
	const ownerName = project.currentUserIsOwner ? 'Me' : textEndEllipsis( ( project.owner as User ).name, 20 );

	const accessProjectAsManager = useAccessProjectAsManager( { project } );
	const onDeleteProjectClick = useDeleteWorkspaceProject( { project } );
	const onLeaveProjectClick = useLeaveWorkspaceProject( { project } );

	const onAccessProjectAsManagerClick = useCallback( () => {
		trackProjectAccessedAsManager( { projectID: project.id } );
		accessProjectAsManager();
	}, [ accessProjectAsManager, project.id ] );

	return {
		usersCount,
		showingShareProjectModal,
		setShowingShareProjectModal,
		canShareProject,
		canDeleteProject,
		canLeaveProject,
		canAccessProjectAsManager,
		createdAt,
		projectName,
		ownerName,
		onDeleteProjectClick,
		onLeaveProjectClick,
		onAccessProjectAsManagerClick
	};
}

export default useWorkspaceProjectListItem;
